import React, { useEffect, useState } from "react";
import { Loader2, FileText, CheckCircle2 } from "lucide-react";

// Phases the user sees:
//   uploading  →   real upload % from axios onUploadProgress
//   processing →   file received, server is extracting/OCR-ing
//   analysing  →   server-side analysis started (used by callers post /analyze)
// Callers set `phase` to reflect where the pipeline is; we cycle friendly
// sub-labels within each phase so the UI never looks frozen on large files.

const PHASE_LABELS = {
  uploading: [
    "Uploading document…",
    "Sending your file securely…",
  ],
  processing: [
    "Processing document…",
    "Extracting text…",
    "Reading embedded slides & images…",
    "Running OCR on scanned pages…",
  ],
  analysing: [
    "Analysing submission…",
    "Checking references…",
    "Generating examiner report…",
  ],
};

/**
 * UploadProgress — a compact, reusable overlay that reassures the student
 * their upload is progressing. Two visual states:
 *   • percent >= 0  → real progress bar
 *   • percent null  → indeterminate stripes (post-upload / analysing phases)
 *
 * Props:
 *   phase    — "uploading" | "processing" | "analysing"
 *   percent  — number 0-100 during upload, null when indeterminate
 *   filename — the file being uploaded (shown in the header)
 *   sizeMB   — file size in MB (helps students on slow WiFi understand the wait)
 */
export const UploadProgress = ({ phase = "uploading", percent = null, filename, sizeMB }) => {
  const labels = PHASE_LABELS[phase] || PHASE_LABELS.uploading;
  const [labelIdx, setLabelIdx] = useState(0);

  // Rotate the sub-label every 3s so the UI feels alive even when the
  // percentage is stuck (e.g. server-side extraction of a large PPTX).
  useEffect(() => {
    setLabelIdx(0);
    if (labels.length <= 1) return;
    const t = setInterval(() => setLabelIdx((i) => (i + 1) % labels.length), 3000);
    return () => clearInterval(t);
  }, [phase, labels.length]);

  const showBar = typeof percent === "number" && !Number.isNaN(percent);

  return (
    <div
      className="rounded-xl border border-indigo-100 bg-indigo-50/40 px-4 py-3.5"
      data-testid="upload-progress"
      data-phase={phase}
      data-percent={showBar ? percent : "indeterminate"}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 border border-indigo-100">
          {showBar && percent >= 100
            ? <CheckCircle2 size={16} className="text-emerald-600" />
            : phase === "uploading"
              ? <FileText size={16} className="text-indigo-600" />
              : <Loader2 size={16} className="text-indigo-600 animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-800 truncate" data-testid="upload-progress-label">
            {labels[labelIdx]}
          </p>
          {filename && (
            <p className="text-[11px] text-zinc-500 truncate">
              {filename}{typeof sizeMB === "number" && sizeMB > 0 ? ` · ${sizeMB.toFixed(1)} MB` : ""}
            </p>
          )}
        </div>
        {showBar && (
          <span
            className="text-xs font-semibold tabular-nums text-indigo-700 shrink-0"
            data-testid="upload-progress-percent"
          >
            {percent}%
          </span>
        )}
      </div>

      <div className="mt-2.5 h-1.5 rounded-full bg-indigo-100 overflow-hidden">
        {showBar ? (
          <div
            className="h-full bg-indigo-600 transition-all duration-200"
            style={{ width: `${Math.max(2, percent)}%` }}
          />
        ) : (
          // Indeterminate shimmer for post-upload phases
          <div className="h-full w-1/3 bg-indigo-500 rounded-full animate-[slide_1.2s_ease-in-out_infinite]"
               style={{ animationName: "myae-indeterminate" }} />
        )}
      </div>

      {/* Local keyframes so we don't need to touch tailwind config */}
      <style>{`
        @keyframes myae-indeterminate {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
};

export default UploadProgress;
